import type { ReactElement } from 'react';
import { Admin, CustomRoutes, Resource } from 'react-admin';
// `react-router-dom` et non `react-router` : c'est le paquet que React Admin importe.
// Sous Vitest, les deux se chargent en deux exemplaires qui ne partagent pas leurs
// contextes, et une `<Route>` de l'un n'est pas reconnue par les `<Routes>` de l'autre.
import { Route } from 'react-router-dom';

import { createHttpClient, type HttpClient } from '@/api/httpClient';
import { createTokenStorage } from '@/api/tokenStorage';
import { HttpClientProvider } from '@/app/providers';
import { ForgotPasswordPage } from '@/auth/ForgotPasswordPage';
import { LoginPage } from '@/auth/LoginPage';
import { ResetPasswordPage } from '@/auth/ResetPasswordPage';
import { createAuthProvider } from '@/auth/authProvider';
import { AdminsScreen } from '@/admin/admins/AdminsScreen';
import { ADMINS_PATH } from '@/admin/admins/routes';
import { AuditLogScreen } from '@/admin/audit/AuditLogScreen';
import { AUDIT_ROUTE } from '@/admin/audit/auditRoutes';
import { ConflictsScreen } from '@/admin/conflicts/ConflictsScreen';
import { CONFLICTS_ROUTE } from '@/admin/conflicts/conflictRoutes';
import { ContentScreen, CONTENT_ROUTE } from '@/admin/content';
import { DataExplorerScreen } from '@/admin/data/DataExplorerScreen';
import { DATA_ROUTE } from '@/admin/data/routes';
import { FamiliesScreen } from '@/admin/families/FamiliesScreen';
import { FAMILIES_ROUTE } from '@/admin/families/routes';
import { FORGOT_PASSWORD_PATH, RESET_PASSWORD_PATH } from '@/auth/routes';
import { FAILED_MESSAGES_PATH, FailedMessagesScreen, HEALTH_PATH, HealthScreen } from '@/admin/operations';
import { PrivacyRequestsScreen } from '@/admin/privacy-requests/PrivacyRequestsScreen';
import { PRIVACY_REQUESTS_PATH } from '@/admin/privacy-requests/routes';
import { GlobalSettingsScreen } from '@/admin/settings';
import { ModerationScreen } from '@/admin/moderation/ModerationScreen';
import { MODERATION_PATH } from '@/admin/moderation/routes';
import { STOCK_PATH } from '@/admin/stock/routes';
import { StockTraceScreen } from '@/admin/stock/StockTraceScreen';
import { AccountsScreen } from '@/admin/users/AccountsScreen';
import { ACCOUNTS_USERS_ROUTE } from '@/admin/users/routes';
import type { DeploymentEnvironment } from '@/config/runtimeConfig';
import { createRestDataProvider } from '@/dataProvider/restDataProvider';
import { asIdString } from '@/formatting/text';
import { i18nProvider } from '@/i18n/i18nProvider';
import { currentBabyIdRef } from '@/resources/babyScope';
import { RESOURCE_CATALOG } from '@/resources/catalog';
import { DashboardScreen, STATISTICS_PATH, StatisticsScreen } from '@/admin/pilotage';
import { SyncSupervisionScreen } from '@/admin/synchronization/SyncSupervisionScreen';
import { SYNC_SUPERVISION_PATH } from '@/admin/synchronization/syncSupervisionApi';
import { DiagnosticPage } from '@/ui/DiagnosticPage';
import { DIAGNOSTIC_PATH, GLOBAL_SETTINGS_PATH } from '@/ui/navigation';
import { BackofficeLayout } from '@/ui/Layout';
import { buildCreate, buildEdit, buildList, buildShow } from '@/ui/ResourceScreens';
import { EnvironmentProvider } from '@/ui/shell/environment';
import { darkTheme, lightTheme } from '@/ui/theme';

/**
 * Composition de l'application.
 *
 * Les ressources ne sont pas ecrites une par une : elles sont derivees du
 * catalogue, et **chaque operation n'est branchee que si le serveur la sert**. Une
 * ressource sans route de creation n'a donc pas de propriete `create`, ce qui fait
 * disparaitre le bouton correspondant partout dans React Admin sans qu'aucun
 * ecran ait a le savoir.
 *
 * Le client HTTP est cree par l'appelant (`main.tsx`) et injecte : c'est lui qui
 * porte l'URL resolue au demarrage, et cela rend l'application montable dans un
 * test avec un double.
 */

export const buildResources = (): ReactElement[] =>
  RESOURCE_CATALOG.map((descriptor) => (
    <Resource
      key={descriptor.name}
      name={descriptor.name}
      options={{ label: descriptor.label }}
      list={descriptor.capabilities.list ? buildList(descriptor) : undefined}
      show={descriptor.capabilities.show ? buildShow(descriptor) : undefined}
      create={descriptor.capabilities.create ? buildCreate(descriptor) : undefined}
      edit={descriptor.capabilities.edit ? buildEdit(descriptor) : undefined}
      // `recordRepresentation` alimente les titres de fiche et les champs de
      // reference : sans lui, React Admin affiche l'identifiant technique.
      recordRepresentation={(record: Record<string, unknown>) => {
        for (const key of ['name', 'firstName', 'code', 'id']) {
          const value = record[key];

          if (typeof value === 'string' && value !== '') {
            return value;
          }
        }

        return asIdString(record.id);
      }}
    />
  ));

/**
 * Ecrans hors ressources React Admin, declares dans `<CustomRoutes>`.
 *
 * Exportes pour le test du registre de navigation : une entree de menu dont la
 * route n'est pas ici menerait a « Page introuvable », et le test le refuse.
 * L'accueil (`/`) n'y figure pas : c'est le tableau de bord, propriete
 * `dashboard` de `<Admin>`.
 */
export const CUSTOM_ROUTES: readonly { readonly path: string; readonly element: ReactElement }[] = [
  { path: SYNC_SUPERVISION_PATH, element: <SyncSupervisionScreen /> },
  // `/*` : la liste et le detail (`MasterDetail`) partagent une seule route.
  { path: CONFLICTS_ROUTE, element: <ConflictsScreen /> },
  // `/*` : la liste et la fiche (`MasterDetail`) partagent une seule route.
  { path: ACCOUNTS_USERS_ROUTE, element: <AccountsScreen /> },
  { path: MODERATION_PATH, element: <ModerationScreen /> },
  { path: FAMILIES_ROUTE, element: <FamiliesScreen /> },
  // `/*` : liste et detail (`MasterDetail`) partagent une seule route, la selection est dans l'URL.
  { path: AUDIT_ROUTE, element: <AuditLogScreen /> },
  // Une seule route `/data/*` : la selection (perimetre, enregistrement) vit dans l'URL.
  { path: DATA_ROUTE, element: <DataExplorerScreen /> },
  // Une seule route `/content/*` : le document ouvert (cgu, cgv) vit dans l'URL.
  { path: CONTENT_ROUTE, element: <ContentScreen /> },
  { path: HEALTH_PATH, element: <HealthScreen /> },
  { path: STATISTICS_PATH, element: <StatisticsScreen /> },
  { path: FAILED_MESSAGES_PATH, element: <FailedMessagesScreen /> },
  { path: PRIVACY_REQUESTS_PATH, element: <PrivacyRequestsScreen /> },
  { path: GLOBAL_SETTINGS_PATH, element: <GlobalSettingsScreen /> },
  { path: STOCK_PATH, element: <StockTraceScreen /> },
  { path: ADMINS_PATH, element: <AdminsScreen /> },
  // Ouvert depuis le menu utilisateur, hors barre laterale.
  { path: DIAGNOSTIC_PATH, element: <DiagnosticPage /> },
];

export const App = ({
  http,
  environment = null,
}: {
  http: HttpClient;
  /** Lu par `resolveEnvironment` au demarrage ; `null` = non renseigne. */
  environment?: DeploymentEnvironment | null;
}): ReactElement => (
  <HttpClientProvider client={http}>
    <EnvironmentProvider value={environment}>
      <Admin
        dataProvider={createRestDataProvider({
          http,
          // Relu a chaque appel : le profil selectionne change sans remonter
          // l'application.
          currentBabyId: () => currentBabyIdRef.get(),
        })}
        authProvider={createAuthProvider(http)}
        i18nProvider={i18nProvider}
        loginPage={LoginPage}
        dashboard={DashboardScreen}
        layout={BackofficeLayout}
        theme={lightTheme}
        darkTheme={darkTheme}
        // Le backoffice est un outil interne : l'indexer n'aurait aucun sens et
        // exposerait sa surface.
        disableTelemetry
        requireAuth
      >
        {/*
          Les ecrans d'administration ne sont PAS des ressources React Admin, et
          c'est deliberé : leur enveloppe de liste rend un `total` reel, la ou les
          neuf ressources metier n'en rendent aucun, et leurs actions —
          « anonymiser », « rejouer », « suspendre l'API » — n'ont aucun equivalent
          dans le vocabulaire `create`/`update`/`delete`. Les y plier produirait des
          ecrans qui mentent sur ce qu'ils font. Voir `src/admin/adminApi.ts`.

          Les chemins viennent des modules eux-memes : les recopier ici ferait
          diverger le menu de la route sans qu'aucun test ne tombe.
        */}
        {/*
          La recuperation du mot de passe se fait **sans session** : ces deux routes
          sont sans coque (`noLayout`), que React Admin rend meme quand personne n'est
          connecte — sans quoi `requireAuth` les renverrait vers la connexion.
        */}
        <CustomRoutes noLayout>
          <Route path={FORGOT_PASSWORD_PATH} element={<ForgotPasswordPage />} />
          <Route path={RESET_PASSWORD_PATH} element={<ResetPasswordPage />} />
        </CustomRoutes>
        <CustomRoutes>
          {CUSTOM_ROUTES.map(({ path, element }) => (
            <Route key={path} path={path} element={element} />
          ))}
        </CustomRoutes>
        {buildResources()}
      </Admin>
    </EnvironmentProvider>
  </HttpClientProvider>
);

/**
 * Fabrique par defaut du client HTTP, utilisee par `main.tsx`.
 *
 * `sessionStorage` : la session meurt avec l'onglet. Compromis assume et explique
 * dans `src/api/tokenStorage.ts`.
 */
export const createDefaultHttpClient = (apiUrl: string): HttpClient =>
  createHttpClient({ apiUrl, storage: createTokenStorage(globalThis.sessionStorage) });
